import format from 'date-fns/format';
import pl from 'date-fns/locale/pl';

import { ConferenceScheduleItem, ConferenceSpeaker } from 'types/models/Conference';
import Typography from '@components/atoms/Typography';
import CheckList from '@components/atoms/CheckList';
import { FormattedMessage } from 'utils/intl';

import classes from './ConferenceSchedule.module.scss';
import messages from './ConferenceSchedule.messages';
import { transformSchedules } from './ConferenceSchedule.utils';

type ScheduleTimeProps = {
  date: string;
  timeFrom?: string;
  timeTo?: string;
};

const timeFormat = 'HH:mm';

const ScheduleTime = ({ date, timeFrom, timeTo }: ScheduleTimeProps) => {
  const dateFrom = timeFrom ? new Date(`${date} ${timeFrom}`) : undefined;
  const dateTo = timeTo ? new Date(`${date} ${timeTo}`) : undefined;

  if (dateFrom && dateTo) {
    if (timeFrom === timeTo) {
      return <>{format(dateFrom, timeFormat)}</>;
    }
    return <>{`${format(dateFrom, timeFormat)} - ${format(dateTo, timeFormat)}`}</>;
  }

  if (dateFrom) {
    return <FormattedMessage {...messages.from} values={{ time: format(dateFrom, timeFormat) }} />;
  }

  if (dateTo) {
    return <FormattedMessage {...messages.to} values={{ time: format(dateTo, timeFormat) }} />;
  }

  return <></>;
};

type Props = {
  items: ConferenceScheduleItem[];
  speakers: ConferenceSpeaker[];
};

const ConferenceSchedule = ({ items, speakers }: Props) => {
  const { schedules, additionalInfo } = transformSchedules(items);

  return (
    <div className={classes.schedules}>
      {schedules.map(({ day, items: scheduleItems }) => (
        <div key={day} className={classes.schedules}>
          <Typography variant="h3" renderAs="div" className={classes.day}>
            {format(new Date(day), 'iiii', { locale: pl })}, {format(new Date(day), 'd.MM.yyyy')}
          </Typography>
          <div className={classes.items}>
            {scheduleItems.map((scheduleItem) => (
              <div key={scheduleItem.id} className={classes.item}>
                <Typography variant="body1" renderAs="div" className={classes.hours}>
                  <ScheduleTime date={day} timeFrom={scheduleItem.timeFrom} timeTo={scheduleItem.timeTo} />
                </Typography>
                <Typography variant="body1" renderAs="div" className={classes.content}>
                  <div className={classes.title}>
                    {scheduleItem.title}
                    {!!scheduleItem.speakers.length && (
                      <i className={classes.speakers}>
                        {scheduleItem.speakers
                          .map(({ speakerId }) => speakers.find(({ id }) => id === speakerId)?.contact.address.fullName)
                          .join(', ')}
                      </i>
                    )}
                  </div>
                  {!!scheduleItem.items?.length && (
                    <CheckList listStyle="unordered" className={classes.list} color="blue">
                      {scheduleItem.items.map(({ text }) => (
                        <CheckList.Item key={text}>{text}</CheckList.Item>
                      ))}
                    </CheckList>
                  )}
                </Typography>
              </div>
            ))}
          </div>
          {!!additionalInfo.length && (
            <Typography variant="body1" renderAs="div" className={classes.additionalInfo}>
              {additionalInfo.map((text, index) => (
                // eslint-disable-next-line react/no-array-index-key
                <p key={`${index}`}>{text}</p>
              ))}
            </Typography>
          )}
        </div>
      ))}
    </div>
  );
};

export default ConferenceSchedule;
